import { useEffect } from 'react';
import { useForm, Resolver } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { yupResolver } from '@hookform/resolvers/yup';
import {
    FormInputText,
    PargodaRoundButton,
    useTheme,
    Form,
    useAppDispatch,
    useAppSelector,
} from '../../../base';
import PargodaModal from '../../PargodaModal';
import {
    ICreateCompanyForm,
    createCompanySchema,
    CreateCompanyFormFields,
} from '../../../common';
import TermsAndConditionsCheckbox from '../../TermsAndConditionsCheckbox';
import {
    ButtonContainer,
    CreateCompanyModalWrapper,
    CustomModalHeader,
    FormContainer,
    SubtitleTypography,
} from './CreateCompanyModal.styles';
import {
    IPostCreateCompanyAction,
    toolsActions,
    toolsSelectors,
} from '../../../actions';

interface Props {
    isModalOpen: boolean;
    setIsModalOpen: (isOpen: boolean) => void;
    noCloseMode?: boolean;
    isFirstTime?: boolean;
}

const CreateCompanyModal = ({
    isModalOpen,
    setIsModalOpen,
    noCloseMode = false,
}: Props) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const dispatch = useAppDispatch();
    const createCompanySuccess = useAppSelector(
        toolsSelectors.getCreateCompanySuccess
    );
    const createCompanyLoading = useAppSelector(
        toolsSelectors.getCreateCompanyLoading
    );

    useEffect(() => {
        if (createCompanySuccess) {
            dispatch(toolsActions.setCreateCompanySuccess());
            setIsModalOpen(false);
        }
    }, [createCompanySuccess, dispatch, setIsModalOpen]);

    const methods = useForm<ICreateCompanyForm>({
        resolver: yupResolver(
            createCompanySchema(t)
        ) as Resolver<ICreateCompanyForm>,
        mode: 'onSubmit',
        defaultValues: {
            name: '',
            acceptTerms: false,
        },
    });

    const { control, reset } = methods;

    useEffect(() => {
        if (isModalOpen) {
            reset({
                name: '',
                acceptTerms: false,
            });
        }
    }, [isModalOpen, reset]);

    const handleFormSubmit = (data: ICreateCompanyForm) => {
        const payload: IPostCreateCompanyAction = {
            name: data[CreateCompanyFormFields.NAME],
        };
        dispatch(toolsActions.postCreateCompany(payload));
    };

    const handleCloseModal = () => {
        if (!noCloseMode) {
            setIsModalOpen(false);
        }
    };

    return (
        <PargodaModal
            modalTitle=""
            isModalOpen={isModalOpen}
            setIsModalOpen={handleCloseModal}
            isDisableCloseButton={noCloseMode}
            sx={{
                width: '500px',
                padding: theme.spacing(3, 3.5),
                borderRadius: '16px',
            }}
        >
            <CreateCompanyModalWrapper>
                <CustomModalHeader variant="h3">
                    {t('tools.addCompanyModal.title')}
                </CustomModalHeader>
                {noCloseMode && (
                    <SubtitleTypography>
                        {t('tools.addCompanyModal.subTitleNew')}
                    </SubtitleTypography>
                )}
                <Form methods={methods} onSubmit={handleFormSubmit}>
                    <FormContainer>
                        <FormInputText
                            name={CreateCompanyFormFields.NAME}
                            control={control}
                            label={t('tools.addCompanyModal.companyName')}
                            required
                            placeholder={t(
                                'tools.addCompanyModal.companyNamePlaceholder'
                            )}
                        />
                        <TermsAndConditionsCheckbox
                            control={control}
                            name={CreateCompanyFormFields.ACCEPT_TERMS}
                            isTools
                        />
                    </FormContainer>
                    <ButtonContainer>
                        <PargodaRoundButton
                            type="submit"
                            variant="contained"
                            loading={createCompanyLoading}
                            disabled={createCompanyLoading}
                            sx={{
                                background: `linear-gradient(0deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                                color: theme.palette.common.white,
                                width: '220px',
                                height: '48px',
                                fontWeight: 'bold',
                                fontSize: '16px',
                                textTransform: 'none',
                            }}
                        >
                            {t('tools.addCompanyModal.createButton')}
                        </PargodaRoundButton>
                    </ButtonContainer>
                </Form>
            </CreateCompanyModalWrapper>
        </PargodaModal>
    );
};

export default CreateCompanyModal;
